import { useEffect, useRef, useState } from "react";
import { Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useJornadaAgora, useVolteiDoIntervalo, type EstadoJornada } from "@/hooks/useJornada";

// A TRAVA DA JORNADA (Douglas 21/10/2026): "a mesma tela grande do aviso, ele
// só fecha quando passar a 1h de intervalo". Avisa 5 min antes do almoço e do
// fechamento; no almoço e fora do expediente cobre o CRM inteiro. A trava de
// verdade é do servidor (auth.middleware); esta tela só mostra e conta o tempo.

const TRAVADOS: EstadoJornada[] = ["almoco", "aguardando_volta", "fora_expediente"];

function relogio(segundos: number) {
  const s = Math.max(0, Math.round(segundos));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

function tocar() {
  try {
    const a = new Audio("/notification-whatsapp.mp3");
    a.volume = 0.5;
    a.play().catch(() => {});
  } catch {
    /* sem áudio disponível */
  }
}

export default function JornadaTrava() {
  const { user } = useAuth();
  const { data, refetch } = useJornadaAgora(Boolean(user));
  const voltei = useVolteiDoIntervalo();
  const [restante, setRestante] = useState<number | null>(null);
  const ultimoEstado = useRef<EstadoJornada | undefined>(undefined);
  const estado = data?.aplica ? data.estado : undefined;

  useEffect(() => {
    setRestante(typeof data?.segundos === "number" ? data.segundos : null);
  }, [data?.segundos, data?.estado]);

  useEffect(() => {
    if (restante === null) return;
    if (restante <= 0) {
      refetch();
      return;
    }
    const t = setTimeout(() => setRestante((s) => (s === null ? null : s - 1)), 1000);
    return () => clearTimeout(t);
  }, [restante, refetch]);

  useEffect(() => {
    if (estado && estado !== ultimoEstado.current && ["aviso_almoco", "aviso_fechamento", "almoco"].includes(estado)) tocar();
    ultimoEstado.current = estado;
  }, [estado]);

  if (!estado) return null;

  if (estado === "aviso_almoco" || estado === "aviso_fechamento") {
    const almoco = estado === "aviso_almoco";
    return (
      <div role="alert" className="fixed left-1/2 top-3 z-[9998] w-[min(92vw,460px)] -translate-x-1/2 rounded-r-lg border border-border border-l-[3px] border-l-primary bg-card p-3 shadow-lg">
        <div className="flex items-start gap-3">
          <Clock className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">
              {almoco ? "Daqui a pouco é o seu intervalo de almoço" : "O CRM fecha daqui a pouco"}
              {restante !== null && <span className="ml-1 tabular-nums text-primary">({relogio(restante)})</span>}
            </p>
            <p className="text-xs text-muted-foreground">
              {almoco ? `${data?.almoco?.inicio} às ${data?.almoco?.fim}. Termine e salve o que está fazendo.` : `Fecha às ${data?.fecha}. Salve o que está fazendo.`}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!TRAVADOS.includes(estado)) return null;

  const voltar = async () => {
    try {
      await voltei.mutateAsync();
      toast.success("Bom trabalho na volta. O CRM está liberado.");
      refetch();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível liberar. Tente de novo.");
    }
  };

  const nome = String((user as { nome?: string } | null)?.nome || "").split(" ")[0];

  return (
    <div role="dialog" aria-modal="true" aria-label="CRM fechado pela jornada" className="fixed inset-0 z-[9999] flex items-center justify-center bg-[linear-gradient(150deg,#5E1424,#3E0E1B)] p-4 text-white">
      <div className="w-full max-w-md text-center">
        {estado === "almoco" && (
          <>
            <p className="text-[11px] font-bold uppercase tracking-[2px] text-white/60">Intervalo de almoço</p>
            <p className="mt-3 text-lg">Bom almoço{nome ? `, ${nome}` : ""}. Bata o ponto na saída.</p>
            <p className="mt-4 font-display text-6xl font-bold tabular-nums">{restante !== null ? relogio(restante) : "–"}</p>
            <p className="mt-1 text-sm text-white/60">para voltar · {data?.almoco?.inicio} às {data?.almoco?.fim}</p>
            <p className="mt-6 rounded-lg bg-white/10 px-3 py-2 text-sm text-white/70">O botão de volta aparece às {data?.almoco?.fim}</p>
          </>
        )}
        {estado === "aguardando_volta" && (
          <>
            <p className="text-[11px] font-bold uppercase tracking-[2px] text-white/60">Intervalo cumprido</p>
            <p className="mt-3 text-lg">Bateu o ponto da volta?</p>
            <button type="button" onClick={voltar} disabled={voltei.isPending}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-white px-4 py-3 text-sm font-bold text-[#5B0A0D] shadow-md transition hover:bg-white/90 disabled:opacity-70">
              {voltei.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Bati meu ponto, estou voltando do intervalo
            </button>
            <p className="mt-3 text-xs text-white/60">O horário do clique fica registrado para o RH.</p>
          </>
        )}
        {estado === "fora_expediente" && (
          <>
            <p className="text-[11px] font-bold uppercase tracking-[2px] text-white/60">Fora do expediente</p>
            <p className="mt-3 text-2xl font-semibold">O CRM abre às {data?.abre}</p>
            <p className="mt-3 text-sm text-white/70">Hora extra ou sábado só com liberação do RH, pedida pelo seu supervisor ou gerente.</p>
          </>
        )}
      </div>
    </div>
  );
}
